import { AcknowledgeReminderState } from 'core/interfaces/IEscalationPolicy';
import { DeleteIcon } from 'icons';
import { Checkbox, Grid, IconButton, InputBlock, Para, Select, Theme } from 'uie/components';

import { notificationTypes } from '../remindAcknowledgeIncidents';
import { useState } from 'react';
import { MultiValue, SingleValue, ActionMeta } from 'react-select';

interface IAcknowledgementReminderRuleProps {
  acknowledgementReminder: AcknowledgeReminderState;
  index: number;
  timeInputHandler: (index: number, value: string) => void;
  notificationHandleOnChange: (
    checkboxIndex: number,
    acknowledgementReminder: AcknowledgeReminderState,
  ) => void;
  removeAcknowledgementReminderHandler: (index: number) => void;
}

type SelectOnChangeHandler = (
  newValue:
    | MultiValue<{
        value: number;
        label: string | number;
      }>
    | SingleValue<{
        value: number;
        label: string | number;
      }>,
  actionMeta: ActionMeta<{
    value: number;
    label: string | number;
  }>,
) => void;

function AcknowledgementReminderRule({
  acknowledgementReminder,
  index,
  timeInputHandler,
  notificationHandleOnChange,
  removeAcknowledgementReminderHandler,
}: IAcknowledgementReminderRuleProps) {
  const { theme } = Theme;

  const totalMinutesToHours = (totalMinutes: number) => Math.floor(totalMinutes / 60);
  const totalMinutesToMinutes = (totalMinutes: number) => totalMinutes % 60;

  const [hours, setHours] = useState(
    totalMinutesToHours(Number(acknowledgementReminder.time)) || 0,
  );
  const [minutes, setMinutes] = useState(
    totalMinutesToMinutes(Number(acknowledgementReminder.time)) || 0,
  );

  const setTotalMinutes = (values?: { hours?: number; minutes?: number }) => {
    const finalHours = isNaN(values?.hours as number) ? hours : (values?.hours as number) || 0;
    const finalMinutes = isNaN(values?.minutes as number)
      ? minutes
      : (values?.minutes as number) || 0;
    const totalMinutes = finalHours * 60 + finalMinutes;
    timeInputHandler(index, String(totalMinutes));
  };

  const onHoursChange: SelectOnChangeHandler = newValue => {
    if (!newValue || !('value' in newValue)) return;

    setHours(newValue.value || 0);

    if (newValue.value === 48) {
      setMinutes(0);
      setTotalMinutes({ hours: newValue.value, minutes: 0 });
    } else {
      setTotalMinutes({ hours: newValue.value });
    }
  };

  const onMinutesChange: SelectOnChangeHandler = newValue => {
    if (!newValue || !('value' in newValue)) return;

    setMinutes(newValue.value || 0);

    setTotalMinutes({ minutes: newValue.value });
  };

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
      }}
      key={acknowledgementReminder.key}
    >
      <Grid style={{ verticalAlign: 'center', alignItems: 'center', marginTop: '12px' }}>
        <p
          style={{
            margin: '0 14px',
            color: '#122146',
            fontSize: '14px',
            fontWeight: 300,
          }}
        >
          Every
        </p>
        <Select
          options={reminderHoursOptions}
          width="80px"
          value={remainderDurationOptionMapper(hours)}
          onChange={onHoursChange}
          isInvalid={acknowledgementReminder.isDuplicateTime}
        />
        <Para fontWeight={300} fontSize={14} style={{ margin: '0 7px' }}>
          Hour(s)
        </Para>
        <Select
          options={reminderMinutesOptions}
          width="80px"
          value={remainderDurationOptionMapper(minutes)}
          onChange={onMinutesChange}
          isDisabled={hours === 48}
          isInvalid={acknowledgementReminder.isDuplicateTime}
        />
        <Para fontWeight={300} fontSize={14} style={{ margin: '0 7px' }}>
          Min(s)
        </Para>
        <Para fontWeight={300} fontSize={14} style={{ marginRight: '7px' }}>
          via
        </Para>
        {notificationTypes.map((notificationType, notificationIndex) => (
          <div key={notificationType.type} style={{ display: 'flex', margin: '0 10px' }}>
            <Checkbox
              style={{ marginRight: '4px' }}
              checked={acknowledgementReminder.notifications[notificationIndex].isChecked}
              onChange={() =>
                notificationHandleOnChange(notificationIndex, acknowledgementReminder)
              }
            />
            <Para>{notificationType.label}</Para>
          </div>
        ))}
      </Grid>

      <IconButton
        onClick={() => removeAcknowledgementReminderHandler(index)}
        style={{ marginTop: 10 }}
      >
        <DeleteIcon height={20} width={20} color={theme.danger.default} />
      </IconButton>
    </div>
  );
}

const remainderDurationOptionMapper = (unit: number) => {
  return {
    value: unit,
    label: Math.floor(unit / 10) === 0 ? `0${unit}` : unit,
  };
};

const reminderHoursOptions = [
  0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 30, 36,
  40, 48,
].map(remainderDurationOptionMapper);
const reminderMinutesOptions = [0, 15, 30, 45].map(remainderDurationOptionMapper);

export default AcknowledgementReminderRule;
